import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    Box,
    Text,
    VStack,
    ListItem,
    UnorderedList,
    useBoolean,
} from '@chakra-ui/react';

import { ToastBoxContext } from '../../contexts/ToastBoxContext';
import PageTitle from '../../components/PageTitle';
import SelectField from '../../components/SelectField';
import { DimensionObj } from '../Authenticated/ManageDimensions';
import Loading from '../../components/Loading';
import Placeholder from '../../components/Placeholder';

import apis from '../../utils/apis';
import { TopicObject } from '../Authenticated/ManageTopics';

function DimensionQuestions() {
    const {
        toast,
        onCloseToast,
    } = useContext(ToastBoxContext);

    // close toast message if open
    useEffect(
        () => {
            if (toast) {
                onCloseToast();
            }
            // setTopicData(topicDataList);
        },
        [onCloseToast, toast],
    );

    const [loadingDimensions, setLoadingDimensions] = useBoolean();
    const [dimensionList, setDimensionList] = useState<DimensionObj[]>();

    const [loadingTopics, setLoadingTopics] = useBoolean();
    const [topicList, setTopicList] = useState<TopicObject[]>();

    const getSavedDimensions = useCallback(
        async () => {
            try {
                setLoadingDimensions.on();
                const apiUrl = '/susaf/dimension/all';
                const response = await apis.get(apiUrl);
                const dataResponse: DimensionObj[] = await response.data;
                if (dataResponse.length <= 0) {
                    setLoadingDimensions.off();
                    return;
                }
                setDimensionList(dataResponse);
                setLoadingDimensions.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR

                console.warn({ error });
            }
        },
        [setLoadingDimensions],
    );

    const dimensionOptions = useMemo(() => {
        if (!dimensionList) {
            return [];
        }
        const mappedDimensionList = dimensionList.map((dimension) => ({
            label: dimension.name,
            value: dimension.id ?? '',
        }));

        const defaultOption = {
            label: 'Select a dimension',
            value: '',
        };

        mappedDimensionList.splice(0, 0, defaultOption);

        return mappedDimensionList;
    }, [dimensionList]);

    useEffect(() => {
        getSavedDimensions();
    }, [getSavedDimensions]);

    const [selectedDimensionId, setSelectedDimensionId] = useState<string>();

    const onSelectDimension = useCallback(
        (value: string) => setSelectedDimensionId(value),
        [],
    );

    const selectedDimensionName = useMemo(() => {
        if (!selectedDimensionId || !dimensionList) {
            return undefined;
        }
        const dimIndex = dimensionList?.findIndex((dL) => dL.id === selectedDimensionId);

        if (dimIndex === -1) {
            return undefined;
        }
        return dimensionList[dimIndex].name;
    }, [dimensionList, selectedDimensionId]);

    const title = !selectedDimensionName ? 'Questions' : `Questions: ${selectedDimensionName}`;

    const getSavedTopics = useCallback(
        async () => {
            try {
                if (!selectedDimensionId) {
                    return;
                }
                setLoadingTopics.on();
                const apiUrl = `/susaf/topic/all?dimensionId=${selectedDimensionId}`;
                const response = await apis.get(apiUrl);
                const dataResponse: TopicObject[] = await response.data;

                if (!dataResponse || dataResponse.length <= 0) {
                    setTopicList(undefined);
                    setLoadingTopics.off();
                    return;
                }
                setTopicList(dataResponse);
                setLoadingTopics.off();
            } catch (error) {
                // TODO: HANDLE THIS ERROR
                console.warn({ error });
            }
        },
        [selectedDimensionId, setLoadingTopics],
    );

    useEffect(() => {
        getSavedTopics();
    }, [getSavedTopics]);

    return (
        <VStack>
            <PageTitle
                title={title}
            />
            {loadingDimensions && (
                <Box width="100%">
                    <Loading message="Loading dimensions" />
                </Box>
            )}
            {dimensionList && dimensionList.length > 0 && (
                <SelectField
                    options={dimensionOptions}
                    label="Select Dimension"
                    onSelectOption={onSelectDimension}
                    value={selectedDimensionId}
                />
            )}
            {dimensionList && dimensionList.length > 0 && !selectedDimensionId && (
                <Box width="100%">
                    <Placeholder text="Select dimension to get topics and questions." />
                </Box>
            )}
            {!loadingTopics && topicList && topicList.length > 0 && topicList.map(
                (item) => (
                    <Box
                        key={item.id}
                        borderWidth={1}
                        width="100%"
                        padding={4}
                    >
                        <Text>
                            <span
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: 16,
                                }}
                            >
                                <em>
                                    {`${item.name} `}
                                </em>
                            </span>
                            <span style={{ fontSize: 16 }}>
                                {item.description}
                            </span>
                        </Text>
                        <UnorderedList
                            paddingX={4}
                            paddingY={2}
                        >
                            {item.question && item.question.map((question) => (
                                <ListItem key={question.id}>
                                    {question.description}
                                </ListItem>
                            ))}
                        </UnorderedList>
                    </Box>
                ),
            )}
            {selectedDimensionId && !loadingDimensions && loadingTopics && (
                <Box width="100%">
                    <Loading message="Loading topics and questions" />
                </Box>
            )}

            {!loadingTopics && selectedDimensionId && !topicList && (
                <Box width="100%">
                    <Placeholder text="Please make sure you have topics and questions for this dimension." />
                </Box>
            )}
        </VStack>
    );
}

export default DimensionQuestions;
